import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListStockLocations,
  useCreateStockLocation,
  useUpdateStockLocation,
  useGetStockLocationsUnplaced,
  useGetStockLocationBalances,
  useListStockLocationTransfers,
  useListInventoryItems,
  getListStockLocationsQueryKey,
  getGetStockLocationBalancesQueryKey,
  type StockLocation,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { ArrowRightLeft, Pencil, Plus } from "lucide-react";
import { ItemCombobox } from "./item-combobox";
import { StockLocationTransferDialog } from "./stock-location-transfer-dialog";
import { ListPagination } from "@/components/common/list-pagination";
import { cn } from "@/lib/utils";

// Phase 58 Plan 13 (LOC-01/02) — the operator-facing locations screen: the
// location list, the drainable Unassigned "unplaced" report, per-item
// balances (visibly summing to the item's total), and the append-only
// transfer log. Every mutation is gated to canManage (ops_lead/admin/
// super_admin, mirrors the server's canManageStockLocations) — a front_desk
// or agent actor sees the list but never the create/edit/transfer affordances.

const KIND_LABEL: Record<string, string> = {
  sale_facing: "Sale-facing",
  reserve: "Reserve",
  unassigned: "Unassigned",
};
const CADENCE_LABEL: Record<string, string> = { daily: "Daily", full_count: "Full-count cycle" };

export function StockLocationsTab() {
  const { user } = useAuth();
  const canManage = user?.role === "ops_lead" || user?.role === "admin" || user?.role === "super_admin";

  const { data: locations = [], isLoading: locationsLoading } = useListStockLocations();
  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const itemNameById = useMemo(() => {
    const m = new Map<number, string>();
    items.forEach((it) => m.set(it.id, it.name));
    return m;
  }, [items]);
  const locationNameById = useMemo(() => {
    const m = new Map<number, string>();
    locations.forEach((l) => m.set(l.id, l.name));
    return m;
  }, [locations]);
  const unassignedLocationId = useMemo(
    () => locations.find((l) => l.isUnassignedBucket)?.id ?? null,
    [locations],
  );

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StockLocation | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSeed, setTransferSeed] = useState<{ itemId: number | null; fromLocationId: number | null }>({
    itemId: null,
    fromLocationId: null,
  });
  function openTransfer(itemId: number | null, fromLocationId: number | null) {
    setTransferSeed({ itemId, fromLocationId });
    setTransferOpen(true);
  }

  const unplacedQuery = useGetStockLocationsUnplaced();

  const [balanceItemId, setBalanceItemId] = useState<number | null>(null);
  const balancesQuery = useGetStockLocationBalances(
    { itemId: balanceItemId ?? 0 },
    {
      query: {
        enabled: balanceItemId != null,
        queryKey: getGetStockLocationBalancesQueryKey({ itemId: balanceItemId ?? 0 }),
      },
    },
  );

  const [transferPage, setTransferPage] = useState(1);
  const [transferPageSize, setTransferPageSize] = useState(25);
  const transfersQuery = useListStockLocationTransfers({ page: transferPage, pageSize: transferPageSize });

  return (
    <div className="space-y-4">
      {/* Location list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Locations</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Pantry, Store Room, Fridge — the per-space storage list. Not tied to the floor plan.
            </p>
          </div>
          {canManage && (
            <Button
              size="sm"
              onClick={() => {
                setEditingLocation(null);
                setLocationDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> Add Location
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {locationsLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Count cadence</TableHead>
                  <TableHead>Status</TableHead>
                  {canManage ? <TableHead className="w-10" /> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-sm font-medium">
                      {l.name}
                      {l.isUnassignedBucket ? (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                          Unassigned
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-sm">{KIND_LABEL[l.kind] ?? l.kind}</TableCell>
                    <TableCell className="text-sm">{CADENCE_LABEL[l.countCadence] ?? l.countCadence}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          l.isActive ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground",
                        )}
                      >
                        {l.isActive ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    {canManage ? (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={`Edit ${l.name}`}
                          onClick={() => {
                            setEditingLocation(l);
                            setLocationDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Unplaced report — CONTEXT.md's "X rupees of stock still unplaced" */}
      <Card>
        <CardHeader>
          <CardTitle>Unplaced stock</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Everything still sitting in Unassigned — drain it deliberately by transferring into a real location.
          </p>
        </CardHeader>
        <CardContent>
          {unplacedQuery.isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (unplacedQuery.data?.items.length ?? 0) === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Nothing unplaced — Unassigned is empty.</div>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <span className="text-sm font-medium text-amber-900">
                  {unplacedQuery.data!.totalQuantity.toLocaleString("en-IN")} units · ₹
                  {unplacedQuery.data!.totalValue.toLocaleString("en-IN")} still unplaced
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Avg cost</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    {canManage ? <TableHead className="w-56" /> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unplacedQuery.data!.items.map((it) => (
                    <TableRow key={it.itemId}>
                      <TableCell className="text-sm">{it.itemName}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{it.quantity}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">₹{it.averageCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        ₹{it.value.toLocaleString("en-IN")}
                      </TableCell>
                      {canManage ? (
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTransfer(it.itemId, unassignedLocationId)}
                          >
                            <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" /> Move stock out of Unassigned
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Per-item balances — the breakdown visibly sums to the total */}
      <Card>
        <CardHeader>
          <CardTitle>Per-item balances</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Pick an item to see its stock broken down by location.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="max-w-sm">
            <ItemCombobox value={balanceItemId} onChange={(id) => setBalanceItemId(id)} placeholder="Pick an item" showTier={false} />
          </div>
          {balanceItemId != null ? (
            balancesQuery.isLoading ? (
              <div className="py-4 text-sm text-muted-foreground">Loading…</div>
            ) : balancesQuery.data ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    {canManage ? <TableHead className="w-32" /> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balancesQuery.data.locations.map((l) => (
                    <TableRow key={l.locationId}>
                      <TableCell className="text-sm">{l.locationName}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{l.quantityOnHand}</TableCell>
                      {canManage ? (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openTransfer(balanceItemId, l.locationId)}>
                            <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" /> Transfer
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                  {/* The total row makes the sum-invariant visible, per this plan's must_haves. */}
                  <TableRow className="bg-muted/60 font-semibold">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{balancesQuery.data.total}</TableCell>
                    {canManage ? <TableCell /> : null}
                  </TableRow>
                </TableBody>
              </Table>
            ) : null
          ) : null}
        </CardContent>
      </Card>

      {/* Transfer log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Transfer log</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Every location-to-location move, append-only.</p>
          </div>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => openTransfer(null, null)}>
              <ArrowRightLeft className="mr-2 h-4 w-4" /> New transfer
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {transfersQuery.isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : (transfersQuery.data?.items.length ?? 0) === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No transfers yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfersQuery.data!.items.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="text-sm">{new Date(t.transferredAt).toLocaleString()}</TableCell>
                    <TableCell className="text-sm">{itemNameById.get(t.itemId) ?? `#${t.itemId}`}</TableCell>
                    <TableCell className="text-sm">{locationNameById.get(t.fromLocationId) ?? `#${t.fromLocationId}`}</TableCell>
                    <TableCell className="text-sm">{locationNameById.get(t.toLocationId) ?? `#${t.toLocationId}`}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{t.quantity}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{t.note ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!transfersQuery.isLoading && (transfersQuery.data?.total ?? 0) > 0 ? (
            <ListPagination
              total={transfersQuery.data!.total}
              page={transferPage}
              pageSize={transferPageSize}
              onPageChange={setTransferPage}
              onPageSizeChange={setTransferPageSize}
              noun={["transfer", "transfers"]}
            />
          ) : null}
        </CardContent>
      </Card>

      <StockLocationTransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        initialItemId={transferSeed.itemId}
        initialFromLocationId={transferSeed.fromLocationId}
      />

      {canManage && (
        <LocationFormDialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen} location={editingLocation} />
      )}
    </div>
  );
}

type EditableKind = "sale_facing" | "reserve";
type EditableCadence = "daily" | "full_count";

function LocationFormDialog({
  open,
  onOpenChange,
  location,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: StockLocation | null;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<EditableKind>("reserve");
  const [countCadence, setCountCadence] = useState<EditableCadence>("full_count");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(location?.name ?? "");
      setKind((location?.kind as EditableKind) ?? "reserve");
      setCountCadence((location?.countCadence as EditableCadence) ?? "full_count");
      setIsActive(location?.isActive ?? true);
    }
  }, [open, location]);

  const createMut = useCreateStockLocation({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListStockLocationsQueryKey() });
        toast({ title: "Location created." });
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        toast({
          title: err instanceof Error ? err.message : "Couldn't create the location.",
          variant: "destructive",
        });
      },
    },
  });
  const updateMut = useUpdateStockLocation({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListStockLocationsQueryKey() });
        toast({ title: "Location updated." });
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        const anyErr = err as { data?: { code?: string; error?: string; heldQuantity?: number } } | undefined;
        toast({
          title:
            anyErr?.data?.code === "location_holds_stock"
              ? `Still holds ${anyErr.data.heldQuantity ?? ""} unit(s) — transfer it out first.`
              : err instanceof Error
                ? err.message
                : "Couldn't update the location.",
          variant: "destructive",
        });
      },
    },
  });

  function onSubmit() {
    if (!name.trim()) return;
    if (location) {
      updateMut.mutate({
        id: location.id,
        data: location.isUnassignedBucket
          ? { isActive } // Unassigned's name/kind/cadence are fixed by migration 0126 — only isActive is editable here.
          : { name: name.trim(), kind, countCadence, isActive },
      });
    } else {
      createMut.mutate({ data: { name: name.trim(), kind, countCadence, isActive: true } });
    }
  }

  const pending = createMut.isPending || updateMut.isPending;
  const isUnassigned = location?.isUnassignedBucket ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{location ? `Edit ${location.name}` : "Add Location"}</DialogTitle>
          <DialogDescription>
            A Fridge, Pantry, or Store Room — an operator-editable list, not tied to the floor plan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fridge" disabled={isUnassigned} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Kind</label>
              <Select value={kind} onValueChange={(v) => setKind(v as EditableKind)} disabled={isUnassigned}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale_facing">Sale-facing</SelectItem>
                  <SelectItem value="reserve">Reserve</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Count cadence</label>
              <Select value={countCadence} onValueChange={(v) => setCountCadence(v as EditableCadence)} disabled={isUnassigned}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="full_count">Full-count cycle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {location ? (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Deactivating refuses if this location still holds stock.</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!name.trim() || pending}>
            {pending ? "Saving…" : location ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
